import { isNotNil } from '@flowio/is';
import type { Serializable } from '@flowio/web-sdk';

import type { CamelCasedPropertiesDeep } from 'type-fest';

import type { CardFormInitOptions } from './actions/card/types';
import type { INLINE_CALLBACK_MESSAGE_TYPE, ErrorCodes } from './constants';

declare global {
  interface Window {
    // webpack EnvironmentPlugin
    process: {
      FLOW_ENV: boolean;
    }
  }
}

export type Nullable<T> = T | null;

export type ValueOf<T> = T[keyof T];

export interface InlineCallbackWrappedMessage<T = unknown> {
  type: typeof INLINE_CALLBACK_MESSAGE_TYPE;
  success: boolean;
  payload: T;
}

export type PrHandlerOpts = {
  session: io.flow.v0.models.OrganizationSession;
};

export interface PaymentRequestCallbackQuery {
  origin?: string;
  flow_payment_request_id?: string;
  flow_payment_request_status?: string;
  flow_origin?: string; // OG Authorization resources
  authorization_key?: string; // OG Authorization resources
  [key: string]: Serializable;
}

/**
 * @description CSS selector | HTMLElement
 */
export type DomReference = string | HTMLElement;

export type DefaultRenderParameterValue = {
  /**
   * @description `buttons` is optional in certain circumstances. e.g. when only a container is
   * needed for rendering (i.e. Card 3DS2 flow).
   */
  buttons?: PaymentButtons;
  /**
   * @description DOMReference where content relating to the payment method should be rendered.
   * Examples: Payment Card form, 3DS2 challenge etc..
   */
  mountContainer: DomReference;
};

export type CardManagedPaymentMethodParameter = {
  id: 'card';
  type: 'managed_form',
  render: Omit<DefaultRenderParameterValue, 'mountButton'>
  & Pick<CardFormInitOptions, 'errorMessages' | 'messages' | 'styles'>
  & {
    button?: DomReference;
    buttonText?: string;
  };
};

export type CardRawForm = Required<Omit<io.flow.v0.models.CardForm, 'challenge_cipher' | 'challenge_text' | 'cipher' | 'requested_currency' | 'metadata' | 'token_type' | 'ip' | 'address'>>;

type CardFormPaymentMethodParameter = {
  id: 'card';
  type: 'raw_form';
  form: CardRawForm;
  render: Omit<DefaultRenderParameterValue, 'mountButton'>;
};

type CardPaymentMethodParameter =
  CardManagedPaymentMethodParameter |
  CardFormPaymentMethodParameter;

type DefaultPaymentMethodParameter = {
  id: Exclude<io.flow.v0.enums.PaymentType, 'card'>;
  render?: DefaultRenderParameterValue;
};

export type PaymentMethodParameter =
  | CardPaymentMethodParameter
  | DefaultPaymentMethodParameter;

export function isNotRedirectOnlyPaymentMethodParameter(
  pmp: PaymentMethodParameter | undefined,
): pmp is CardPaymentMethodParameter | DefaultPaymentMethodParameter {
  return isNotNil(pmp)
    && isNotNil((pmp as CardPaymentMethodParameter | DefaultPaymentMethodParameter).render);
}

type RequestPaymentDataParameter = Pick<
CamelCasedPropertiesDeep<io.flow.v0.models.PaymentRequest>,
'amount' | 'currency' | 'orderInformation' | 'returnUrl' | 'reference'
> & {
  paymentMethod?: PaymentMethodParameter;
};

type RequestPaymentConfigParameter = {
  /**
   * @description Flow Organization key.
   */
  organization: string;
  /**
   * @description Two character lang locale.
   */
  locale: string;
  /**
   * @description Flow Session token.
   */
  sessionId: string;

  /**
   * @description Actions that the client can support (i.e. 3DS2 operations etc.)
   */
  supportedActions?: io.flow.v0.enums.PaymentActionType[];
};

export type PaymentRequestError = {
  error: {
    message: string;
    code: ErrorCodes | string;
  }
};

export type RequestPaymentReturnValue = {
  request: CamelCasedPropertiesDeep<io.flow.v0.models.PaymentRequest>;
};

type RequestPaymentFailure = {
  type: 'request_payment_failure',
  failure: CamelCasedPropertiesDeep<io.flow.v0.models.PaymentFailure>;
};

type PaymentRequestErrorWrapper = {
  type: 'request_payment_error',
  error: PaymentRequestError
};

export type RequestPaymentErrorReturnValue = RequestPaymentFailure | PaymentRequestErrorWrapper;

interface PaymentRequestHandler {
  (params: RequestPaymentReturnValue): void;
}

interface PaymentRequestFailureHandler {
  (error: RequestPaymentErrorReturnValue): void;
}

export type RequestPaymentParameters = {
  data: RequestPaymentDataParameter;
  config: RequestPaymentConfigParameter;
  onPaymentRequested?: PaymentRequestHandler;
  onPaymentMethodCompleted?: PaymentRequestHandler;
  onFailure?: PaymentRequestFailureHandler;
};

export type PaymentButtons = {
  primary: DomReference;
  secondary?: DomReference;
};

export type BaseSDKActionParams = {
  mountContainer?: DomReference,
  buttons?: PaymentButtons,
};
